import React, { useMemo } from 'react';
import { MoreOutlined } from '@ant-design/icons';
import { Breadcrumb, Button, Dropdown, Input, RMTabs, Tooltip } from '../../components';
import { applyButtonState, getDocumentationDropDownMenuItems } from './helpers';

import './TopBar.scss';

const { Search } = Input;

/**
 * Reusable page top bar: an optional breadcrumb row, an optional tabs row,
 * and an actions row (primary/secondary buttons, an overflow menu, and an
 * optional search input).
 *
 * `tabs` renders as route-linked tabs (real navigation) when tabsMode is
 * "route", otherwise as an in-page section switcher (no navigation, reports
 * the active key via onTabChange).
 *
 * `children`, if provided, renders after the actions row as a catch-all for
 * content that doesn't fit the config above.
 */
export const TopBar = ({
    breadcrumbItems = [],
    tabs = [],
    tabsMode = 'section',
    activeKey,
    defaultActiveKey,
    onTabChange,
    primaryButtons: primaryButtonsConfig = [],
    secondaryButtons: secondaryButtonsConfig = [],
    actions = {},
    selectedCount = 0,
    resourceNouns,
    dropDownMenuItems,
    documentationType,
    search,
    children,
}) => {
    const primaryButtons = useMemo(
        () =>
            applyButtonState(primaryButtonsConfig, selectedCount, resourceNouns).map((config) => ({
                ...config,
                onClick: actions[config.key],
            })),
        [primaryButtonsConfig, selectedCount, resourceNouns, actions],
    );
    const secondaryButtons = useMemo(
        () =>
            applyButtonState(secondaryButtonsConfig, selectedCount, resourceNouns, {
                defaultSelectionDependent: true,
            }).map((config) => ({
                ...config,
                onClick: actions[config.key],
            })),
        [secondaryButtonsConfig, selectedCount, resourceNouns, actions],
    );
    const resolvedDropDownMenuItems = useMemo(
        () => [
            ...(documentationType ? getDocumentationDropDownMenuItems(documentationType) : []),
            ...(dropDownMenuItems ?? []),
        ],
        [documentationType, dropDownMenuItems],
    );

    return (
        <div className="rm-top-bar">
            {breadcrumbItems.length > 0 && (
                <Breadcrumb className="rm-top-bar-breadcrumb" separator="/" items={breadcrumbItems} />
            )}
            {tabs.length > 0 && (
                <RMTabs
                    className="rm-top-bar-tabs"
                    tabs={tabs}
                    isNav={tabsMode === 'route'}
                    activeKey={activeKey}
                    defaultActiveKey={defaultActiveKey}
                    onChange={onTabChange}
                />
            )}
            <div className="rm-top-bar-actions">
                {primaryButtons.map(({ key, tooltip, buttonText, ...action }, index) => (
                    <Tooltip key={key ?? index} title={tooltip}>
                        <Button type="primary" {...action}>
                            {buttonText}
                        </Button>
                    </Tooltip>
                ))}
                {secondaryButtons.map(({ key, tooltip, buttonText, ...action }, index) => (
                    <Tooltip key={key ?? index} title={tooltip}>
                        <Button {...action}>{buttonText}</Button>
                    </Tooltip>
                ))}
                {resolvedDropDownMenuItems.length > 0 && (
                    <Dropdown
                        menu={{ items: resolvedDropDownMenuItems, className: 'rm-top-bar-more-menu' }}
                        trigger={['click']}
                        placement="bottomLeft"
                    >
                        <Button type="text" icon={<MoreOutlined className="rm-top-bar-more-icon" />} />
                    </Dropdown>
                )}
                {search && (
                    <div className="rm-top-bar-search">
                        <Search
                            allowClear
                            className={search.className}
                            placeholder={search.placeholder}
                            value={search.value}
                            onChange={search.onChange}
                            onSearch={search.onSearch}
                            style={search.width ? { width: search.width } : undefined}
                        />
                    </div>
                )}
            </div>
            {children}
        </div>
    );
};
